/**
 * Cliente de la API privada.
 *
 * La sesion viaja en una cookie `HttpOnly` que el navegador manda solo
 * (`credentials: "include"`): este codigo no la ve ni la puede leer, que es lo
 * que impide que un script inyectado se la lleve. El backend la verifica en
 * cada peticion y decide que puede hacer el usuario: aca no se toma ninguna
 * decision de permisos (D-063).
 */

import { ApiRequestError, API_URL } from "./api";
import type { ApiErrorPayload, AvailabilitySlot } from "./api";

/**
 * Se dispara cuando la API dice que no hay sesion (401) en una ruta privada:
 * la sesion vencio, se cerro desde otro dispositivo o cambio la contraseña.
 * `lib/auth.tsx` lo escucha y manda a ingresar, en lugar de dejar cada
 * pantalla en "no pudimos cargar esta seccion".
 */
export const SESION_PERDIDA = "turnpro:sesion-perdida";

export interface ApiClient {
  get<T>(path: string, signal?: AbortSignal): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string, body?: unknown): Promise<T>;
  /**
   * Un archivo de una ruta privada: la imagen de un comprobante, por ejemplo.
   *
   * Existe porque una imagen de la API privada no se puede poner directo en un
   * `<img src>`: en desarrollo la API vive en otro origen y la cookie no
   * viaja con la imagen, y aun en produccion conviene que el error de
   * permisos se vea como error y no como un icono roto. Se pide como
   * cualquier otra peticion y recien despues se dibuja, con
   * `URL.createObjectURL`.
   */
  blob(path: string, signal?: AbortSignal): Promise<Blob>;
}

/**
 * La peticion en crudo, con la cookie de sesion y los errores de la API ya
 * traducidos. Lo que devuelve el cuerpo —JSON o bytes— lo decide quien llama.
 */
async function pedir(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<Response | null> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      // La cookie de sesion. En produccion la API esta en el mismo origen
      // (`/api`); en desarrollo, en otro puerto del mismo sitio.
      credentials: "include",
      ...(signal ? { signal } : {}),
      headers: {
        // Solo cuando hay cuerpo: declarar JSON sin mandarlo hace que el
        // servidor corte el pedido antes de la ruta. Afecta a todo lo que no
        // manda nada — dar de baja el debito, borrar un bloqueo, invitar a
        // alguien del equipo.
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiRequestError(0, {
      code: "NETWORK_ERROR",
      message: "No pudimos conectarnos con el servidor.",
    });
  }

  // Sin contenido: no hay cuerpo que leer.
  if (response.status === 204) return null;

  if (!response.ok) {
    let payload: ApiErrorPayload = { code: "UNKNOWN", message: "Ocurrio un error inesperado." };
    try {
      const parsed = (await response.json()) as { error?: ApiErrorPayload };
      if (parsed.error) payload = parsed.error;
    } catch {
      // Sin cuerpo JSON: queda el mensaje generico.
    }
    if (response.status === 401 && !path.startsWith("/v1/auth/") && typeof globalThis.dispatchEvent === "function") {
      globalThis.dispatchEvent(new Event(SESION_PERDIDA));
    }
    throw new ApiRequestError(response.status, payload);
  }

  return response;
}

async function send<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await pedir(method, path, body, signal);
  if (!response) return undefined as T;

  /*
   * Lo mismo que en `apps/admin`: si lo que vuelve no es JSON, el
   * `SyntaxError` pelado se veria como un error inesperado y se perderia el
   * dato que importa — que la respuesta no vino de la API.
   */
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiRequestError(response.status, {
      code: "INVALID_RESPONSE",
      message:
        "El servidor respondio algo que no es JSON. Suele significar que el pedido no llego a la API.",
    });
  }
}

export function createApiClient(): ApiClient {
  return {
    get: (path, signal) => send("GET", path, undefined, signal),
    post: (path, body) => send("POST", path, body),
    patch: (path, body) => send("PATCH", path, body),
    put: (path, body) => send("PUT", path, body),
    del: (path, body) => send("DELETE", path, body),
    blob: async (path, signal) => {
      const response = await pedir("GET", path, undefined, signal);
      if (!response) {
        throw new ApiRequestError(204, {
          code: "EMPTY_RESPONSE",
          message: "El servidor no devolvio ningun archivo.",
        });
      }
      return response.blob();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Tipos de la API privada                                                     */
/* -------------------------------------------------------------------------- */

export interface AdminTenant {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly businessType: string;
  readonly timezone: string;
  readonly description: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly address: string | null;
  readonly socialLinks: Record<string, string>;
  /** Logo, portada y color de la pagina publica. Ver `lib/branding.ts`. */
  readonly branding: Record<string, unknown>;
  readonly terminology: Record<string, string>;
  readonly bookingSettings: BookingSettings;
  /**
   * El programa de cortesias. Opcional porque durante un despliegue el
   * navegador puede tener el bundle nuevo y la API todavia la version
   * anterior: sin el se asume apagado, que es lo que no promete nada.
   */
  readonly loyalty?: LoyaltySettings;
  /**
   * Los campos propios de la ficha que declaro el negocio. Opcional por lo
   * mismo que las cortesias: sin el se asume que no hay ninguno, que es lo que
   * deja la ficha como estaba.
   */
  readonly customerFields?: CustomerField[];
  /**
   * Que parte de los datos del negocio ve su equipo. Opcional por lo mismo que
   * el resto: sin el se asume lo que el producto ya hacia.
   */
  readonly privacy?: { readonly showRevenueToTeam: boolean; readonly showInSearch?: boolean };
  /**
   * Lo que eligio el negocio sobre las secciones opcionales del panel. Sin
   * clave es "lo que corresponda por rubro y plan". Lo que resulta viene en
   * `SessionContext.capabilities`.
   */
  readonly capabilityOverrides?: { readonly [K in PanelCapability]?: boolean };
  /** Alias, CBU y a nombre de quien. Se publica en la pagina del negocio. */
  readonly paymentInstructions: string | null;
  readonly active: boolean;
}

/* -------------------------------------------------------------------------- */
/* Campos propios de la ficha                                                  */
/* -------------------------------------------------------------------------- */

export type CustomerFieldType = "text" | "longtext" | "number" | "date" | "select" | "boolean";

/** Lo que se puede guardar en un campo. Un tipo, una forma. */
export type CustomerFieldValue = string | number | boolean;

export interface CustomerField {
  /**
   * El nombre interno, estable. Lo calcula el servidor la primera vez y **no
   * cambia** aunque despues se renombre el campo: es la llave de los valores ya
   * guardados. Un campo nuevo todavia sin guardar no la tiene.
   */
  readonly key?: string;
  readonly label: string;
  readonly type: CustomerFieldType;
  /** Solo con `select`. */
  readonly options?: string[];
  readonly hint?: string;
}

export const CUSTOMER_FIELD_TYPE_LABELS: Record<CustomerFieldType, string> = {
  text: "Texto",
  longtext: "Texto largo",
  number: "Numero",
  date: "Fecha",
  select: "Lista de opciones",
  boolean: "Si / No",
};

/** El tope que acepta el servidor. La pantalla lo dice antes de un 400. */
export const MAX_CUSTOMER_FIELDS = 12;

/** Que se le da a quien vuelve: un porcentaje, un importe o el servicio. */
export type RewardKind = "percent" | "amount" | "free";

export interface LoyaltySettings {
  /** Apagado de fabrica: regalar plata se activa a proposito. */
  readonly enabled: boolean;
  /** Cuantas reservas cumplidas hacen falta para ganarse una cortesia. */
  readonly threshold: number;
  readonly rewardKind: RewardKind;
  /** El porcentaje con `percent`, los centavos con `amount`. */
  readonly rewardValue: number;
  /** Etiqueta propia del negocio. Vacia: la arma el servidor. */
  readonly label: string;
}

/** Los valores con los que arranca el formulario de cortesias. */
export const LOYALTY_DEFAULTS: LoyaltySettings = {
  enabled: false,
  threshold: 10,
  rewardKind: "percent",
  rewardValue: 0,
  label: "",
};

/** Que se le pide a quien reserva antes de guardarle la hora. */
export type PrepaymentMode = "none" | "deposit" | "full";

export interface BookingSettings {
  readonly slotIntervalMinutes: number;
  readonly minNoticeMinutes: number;
  readonly maxAdvanceDays: number;
  readonly allowGuestBooking: boolean;
  readonly prepaymentMode: PrepaymentMode;
  /** Porcentaje del precio del servicio que se pide de seña. */
  readonly depositPercent: number;
  /** Descuento por pagar el servicio completo por adelantado. */
  readonly fullPaymentDiscountPercent: number;
  /** Si quien reservo puede cancelar desde el enlace de su reserva. */
  readonly allowCustomerCancellation: boolean;
  /** Antelacion minima, en minutos, para que pueda cancelar. */
  readonly cancellationMinNoticeMinutes: number;
  /** Si quien reservo puede cambiar el horario desde el enlace de su reserva. */
  readonly allowCustomerReschedule: boolean;
  /** Antelacion minima, en minutos, para que pueda moverlo. */
  readonly rescheduleMinNoticeMinutes: number;
  /**
   * Con cuantas horas de anticipacion sale el recordatorio. `null` es "la de
   * la instalacion" y `0` que este negocio no manda recordatorios.
   */
  readonly reminderHoursBefore: number | null;
  /**
   * Si el recordatorio sale tambien por WhatsApp. Opcional por compatibilidad
   * con una API anterior, que no lo manda: se lee como apagado.
   */
  readonly whatsappReminders?: boolean;
}

/** Como viene el pago por adelantado de un turno. */
export interface AppointmentPayment {
  readonly kind: "deposit" | "full";
  readonly status: "pending" | "declared" | "confirmed" | "rejected";
  readonly amountCents: number;
  readonly amountLabel: string;
  readonly listPriceCents: number;
  readonly discountCents: number;
  readonly discountLabel: string | null;
  readonly balanceCents: number;
  readonly balanceLabel: string | null;
  readonly method: string;
  readonly reference: string | null;
  /**
   * Si hay comprobante adjunto. No viene la imagen: la agenda trae decenas de
   * turnos y serian megas en una respuesta que casi siempre nadie mira.
   *
   * Opcional porque durante un despliegue el navegador puede tener el bundle
   * nuevo y la API todavia la version anterior.
   */
  readonly hasReceipt?: boolean;
  readonly declaredAt: string | null;
  readonly reviewedAt: string | null;
  readonly note: string | null;
}

/** Que puede hacer hoy el negocio segun como este su suscripcion. */
export type BillingAccess = "full" | "warned" | "restricted";

export interface BillingSummary {
  readonly status: "trialing" | "active" | "past_due" | "suspended" | "canceled";
  readonly access: BillingAccess;
  readonly inTrial: boolean;
  readonly daysUntilBlock: number | null;
  readonly validUntil: string | null;
}

/**
 * Las secciones del panel que se pueden dar o quitar a alguien del equipo.
 * Las claves son las de la API (`@turnpro/core`, `PANEL_SECTIONS`).
 */
export type PanelSection =
  | "agenda"
  | "caja"
  | "clientes"
  | "metricas"
  | "servicios"
  | "productos"
  | "recursos"
  | "equipo"
  | "facturacion"
  | "configuracion"
  | "suscripcion";

export type AccessRole = "admin" | "member";

/** Alguien que ya entra al panel. */
export interface TeamMember {
  readonly userId: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly imageUrl: string | null;
  readonly role: AccessRole;
  /** `null` es ver todo. */
  readonly sections: PanelSection[] | null;
  readonly professional: { readonly id: string; readonly name: string } | null;
  readonly isYou: boolean;
  readonly joinedAt: string;
}

export type InvitationChannel = "whatsapp" | "email" | "link";

export interface TeamInvitation {
  readonly id: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly channel: InvitationChannel;
  readonly role: AccessRole;
  readonly sections: PanelSection[] | null;
  readonly professional: { readonly id: string; readonly name: string } | null;
  readonly status: "pending" | "accepted" | "revoked" | "expired";
  readonly expiresAt: string;
  readonly createdAt: string;
}

export interface TeamAccess {
  readonly members: TeamMember[];
  readonly invitations: TeamInvitation[];
  /** Si el correo de la invitacion sale solo, o hay que mandarlo a mano. */
  readonly emailEnabled: boolean;
}

/** Lo que devuelve crear o renovar una invitacion. El enlace solo viaja aca. */
export interface CreatedInvitation {
  readonly invitation: TeamInvitation;
  /** `/invitacion/<enlace>`: la URL completa se arma con el origen de la app. */
  readonly path: string;
  readonly emailSent: boolean;
}

/** Lo que dice un enlace de invitacion, sin sesion. */
export interface InvitationPreview {
  readonly business: { readonly name: string; readonly logoUrl: string | null };
  readonly name: string | null;
  readonly role: AccessRole;
  readonly status: "pending" | "accepted" | "revoked" | "expired";
  readonly expiresAt: string;
}

/** Un negocio al que entra la cuenta, para el selector. */
export interface BusinessOption {
  readonly id: string;
  readonly name: string;
  readonly role: AccessRole;
  readonly active: boolean;
}

export interface SessionContext {
  readonly userId: string;
  /** Los negocios de la cuenta. Opcional por compatibilidad durante un despliegue. */
  readonly businesses?: BusinessOption[];
  /** Si la cuenta tiene el segundo factor. Opcional por lo mismo. */
  readonly mfaEnabled?: boolean;
  /**
   * Las secciones del panel que ve esta sesion. Arma el menu; quien decide de
   * verdad es la API en cada ruta. Opcional por compatibilidad durante un
   * despliegue: sin el campo se lee como "todas", que es lo de antes.
   */
  readonly sections?: PanelSection[];
  readonly organization: { id: string; slug: string | null; role: string | null } | null;
  readonly isAdmin: boolean;
  readonly tenant: AdminTenant | null;
  /**
   * Que canales de aviso automatico estan activos en esta instalacion. Sin
   * correo configurado, confirmar un turno no le avisa nada al cliente.
   *
   * Opcional porque durante un despliegue el navegador puede tener el bundle
   * nuevo y la API todavia la version anterior: se asume apagado, que es la
   * lectura que no le hace prometer al negocio un aviso que no salio.
   */
  readonly notifications?: {
    readonly email: boolean;
    /**
     * Si esta instalacion manda recordatorios. Apagado significa que la
     * antelacion que configure el negocio no la va a respetar nadie, asi que
     * la pantalla no la ofrece. Opcional por la misma razon que `email`.
     */
    readonly reminders?: boolean;
    /** Antelacion que rige para quien no eligio la suya, en horas. */
    readonly reminderHoursBefore?: number;
    /** Si esta instalacion manda recordatorios por WhatsApp. */
    readonly whatsapp?: boolean;
  };
  /**
   * La ficha del equipo que corresponde a esta sesion, cuando quien entro es
   * alguien que atiende. Con esto puesto, la API ya recorta la agenda y el
   * resumen a sus propios turnos: la pantalla no ofrece filtros que no van a
   * tener efecto.
   *
   * Opcional por compatibilidad durante un despliegue: un bundle nuevo contra
   * una API vieja lo lee como "sin recorte", que es como se comportaba antes.
   */
  readonly professional?: { readonly id: string; readonly name: string } | null;
  /** `null` mientras la organizacion todavia no tiene negocio. */
  readonly billing: BillingSummary | null;
  /**
   * Que secciones opcionales tiene el panel de este negocio y por que. Las
   * resuelve la API con el rubro, lo elegido, el plan y los datos cargados.
   * Ordenan el menu; no son un permiso.
   *
   * Opcional por compatibilidad durante un despliegue: sin el campo se ve
   * todo, que es lo que el panel hacia antes.
   */
  readonly capabilities?: PanelCapabilities | null;
  /**
   * Lo opcional de la caja que esta listo para usarse: el QR de Mercado Pago
   * del negocio y la factura electronica. Opcional por compatibilidad: sin el
   * campo no se ofrece ninguno de los dos botones.
   */
  readonly cobros?: CobrosDisponibles | null;
}

export interface CobrosDisponibles {
  readonly qr: { readonly mode: QrMode } | null;
  readonly invoicing: { readonly environment: "homologacion" | "produccion"; readonly ivaCondition: string } | null;
}

/** Las secciones del panel que un negocio puede no usar. */
export type PanelCapability = "usesTeam" | "usesResources" | "sellsProducts";

/** De donde sale que una seccion se vea: los datos, lo elegido, el plan o el rubro. */
export type CapabilitySource = "data" | "settings" | "plan" | "profile";

export type PanelCapabilities = {
  readonly [K in PanelCapability]: { readonly enabled: boolean; readonly source: CapabilitySource };
};

/** El saldo de mensajes de WhatsApp de un negocio (D-061). */
export interface WhatsappCredits {
  /** Si esta instalacion manda WhatsApp. Apagado, la pantalla no muestra nada. */
  readonly enabled: boolean;
  /** Si se puede comprar un paquete con Mercado Pago desde el panel (D-072). */
  readonly onlinePurchase: boolean;
  readonly balance: number;
  readonly consumedThisMonth: number;
  /** El cupo que trae su plan cada mes. 0 si no trae. */
  readonly includedMonthly: number;
  readonly lowBalanceThreshold: number;
  readonly movements: readonly {
    readonly id: string;
    readonly kind: string;
    readonly label: string;
    readonly quantity: number;
    readonly createdAt: string;
  }[];
  readonly packages: readonly {
    readonly id: string;
    readonly name: string;
    readonly messages: number;
    readonly priceLabel: string;
    readonly pricePerMessageLabel: string;
  }[];
}

export interface SubscriptionPayment {
  readonly id: string;
  readonly amountCents: number;
  readonly amountLabel: string;
  readonly currency: string;
  readonly method: string;
  readonly status: string;
  readonly paidAt: string;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly note: string | null;
}

/**
 * El plan con sus importes ya calculados por la API.
 *
 * Ningun componente vuelve a hacer la cuenta: si el descuento se aplicara en
 * el navegador, la pantalla podria mostrar un numero distinto del que se
 * cobra.
 */
export interface PlanQuote {
  /** Solo viene en el listado de planes que se pueden contratar. */
  readonly id?: string;
  readonly code: string;
  readonly name: string;
  readonly currency: string;
  readonly intervalMonths: number;
  /**
   * Si se puede contratar hoy. El plan de un negocio puede estar fuera de la
   * venta: lo sigue pagando al precio que eligio (D-059).
   */
  readonly available: boolean;

  /** Cuanta gente admite el plan. `null` es sin limite. */
  readonly maxProfessionals: number | null;
  readonly maxProfessionalsLabel: string;

  /** Precio de lista del periodo, sin descuentos. */
  readonly priceCents: number;
  readonly priceLabel: string;

  /** Lo que se cobra en el primer pago, con la promocion de entrada. */
  readonly firstPaymentCents: number;
  readonly firstPaymentLabel: string;
  readonly firstPaymentDiscountCents: number;
  readonly firstPaymentDiscountLabel: string;
  readonly firstPeriodDiscountPercent: number;

  /** Lo que se cobra de ahi en adelante. */
  readonly recurringCents: number;
  readonly recurringLabel: string;
  readonly prepayDiscountPercent: number;

  readonly monthlyEquivalentCents: number;
  readonly monthlyEquivalentLabel: string;
}

/** Cuanta gente atiende hoy y cuanta admite el plan contratado. */
export interface TeamCapacity {
  readonly count: number;
  readonly limit: number | null;
  readonly remaining: number | null;
  readonly fits: boolean;
}

export interface SubscriptionDetail extends BillingSummary {
  readonly blocksAt: string | null;
  readonly provider: string;
  readonly autoRenews: boolean;
  /** Lo que autorizo en Mercado Pago al adherir. `null` si no se sabe. */
  readonly autopayAmountCents: number | null;
  /** El debito sigue cobrando un importe que ya no es el de su plan. */
  readonly autopayOutdated: boolean;
  readonly priceLabel: string;
  readonly mercadoPagoAvailable: boolean;
  readonly plan: PlanQuote;
  readonly team: TeamCapacity;
  /** Los escalones que el negocio puede contratar. */
  readonly plans: readonly PlanQuote[];
  readonly payments: readonly SubscriptionPayment[];
}

export interface AdminService {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly durationMinutes: number;
  readonly bufferBeforeMinutes: number;
  readonly bufferAfterMinutes: number;
  readonly priceCents: number | null;
  readonly currency: string;
  readonly category: string | null;
  readonly displayOrder: number;
  readonly active: boolean;
  /** Si el turno necesita si o si un recurso libre para poder reservarse. */
  readonly requiresResource: boolean;
}

export interface AdminProfessional {
  readonly id: string;
  readonly name: string;
  /** Opcional. Es a donde se le manda la invitacion para entrar al panel. */
  readonly email: string | null;
  readonly headline: string | null;
  readonly bio: string | null;
  readonly avatarUrl: string | null;
  readonly displayOrder: number;
  readonly active: boolean;
  readonly serviceIds: readonly string[];
  /** Ya acepto la invitacion y entra al panel con su cuenta. */
  readonly hasAccess: boolean;
  /** Se le mando la invitacion y todavia no la acepto. */
  readonly invitedAt: string | null;
  /** Lo que cobra por servicios y por productos. Solo llega a quien administra. */
  readonly commissionPercent?: number | null;
  readonly productCommissionPercent?: number | null;
}

/**
 * Un recurso: sala, cabina, consultorio, sillon, puesto.
 *
 * No es una persona: no tiene correo ni entra al panel. Lo unico que aporta es
 * cuantos turnos del mismo tipo se pueden atender a la vez.
 */
export interface AdminResource {
  readonly id: string;
  readonly name: string;
  /** "Sala", "Cabina", "Sillon": como lo llama el negocio. */
  readonly kind: string | null;
  readonly description: string | null;
  readonly displayOrder: number;
  readonly active: boolean;
  readonly serviceIds: readonly string[];
}

export interface AgendaAppointment {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: string;
  /**
   * Quien lo cancelo: `customer` si el cliente uso su enlace, `business` si
   * fue el negocio. Vacio mientras el turno siga en pie.
   */
  readonly cancelledBy: string | null;
  readonly notes: string | null;
  readonly serviceId: string;
  readonly serviceName: string;
  readonly professionalId: string | null;
  readonly professionalName: string | null;
  readonly customerId: string;
  readonly customerName: string;
  readonly customerEmail: string | null;
  readonly customerPhone: string | null;
  /**
   * El telefono armado para WhatsApp, con codigo de pais (`5491155551234`).
   * Lo arma la API con el pais del negocio; `null` si el telefono no alcanza.
   * Opcional: una API anterior no lo manda.
   */
  readonly customerWhatsapp?: string | null;
  /**
   * Cuando quien reservo dijo "voy" desde el recordatorio de WhatsApp. No es
   * el estado `confirmed`, que lo decide el negocio.
   */
  readonly customerConfirmedAt?: string | null;
  /** Lo que se pidio por adelantado. `null` si el negocio no cobra al reservar. */
  readonly payment: AppointmentPayment | null;
  /**
   * La cortesia que lleva este turno, si a esa persona le tocaba una. Se aplica
   * sola al cargarlo y se consume recien cuando el turno se cumple.
   *
   * Opcional porque durante un despliegue el navegador puede tener el bundle
   * nuevo y la API todavia la version anterior.
   */
  readonly reward?: CustomerReward | null;
  /** El ticket con que se cobro, o `null` si todavia no paso por la caja. */
  readonly sale?: AppointmentSaleSummary | null;
}

/**
 * El horario de un dia: cuando abre el negocio y que queda libre.
 *
 * Lo calcula el servidor con la misma funcion que la vista semanal, asi que el
 * dia y la semana no pueden discrepar. El navegador **no** recalcula huecos:
 * uno que la pantalla ofrezca y el motor despues rechace es peor que no
 * mostrar ninguno.
 */
export interface AgendaDaySchedule {
  readonly date: string;
  /** 0 = domingo. */
  readonly weekday: number;
  /** El negocio no abre ese dia, o hay un cierre. */
  readonly closed: boolean;
  /** Franjas abiertas, en minutos desde la medianoche del negocio. */
  readonly windows: readonly TimeWindow[];
  /** Lo que queda libre dentro de esas franjas. */
  readonly gaps: readonly TimeWindow[];
  readonly openMinutes: number;
  readonly busyMinutes: number;
  readonly freeMinutes: number;
}

export interface AgendaResponse {
  readonly timezone: string;
  readonly from: string;
  readonly to: string;
  readonly appointments: readonly AgendaAppointment[];
  /**
   * El horario del dia, cuando se pidio un dia suelto y sin filtrar por
   * estado. `null` o ausente en cualquier otro caso — y tambien mientras dure
   * un despliegue, donde el navegador puede tener el bundle nuevo y la API
   * todavia la version anterior. La pantalla tiene que seguir funcionando sin
   * el: simplemente no muestra los huecos.
   */
  readonly schedule?: AgendaDaySchedule | null;
}

/**
 * Turnos encontrados por nombre, correo o telefono, sobre todas las fechas.
 *
 * Sin `from` ni `to`: lo que se busca suele ser el ultimo turno de alguien, que
 * pudo ser ayer o el año pasado.
 */
export interface AgendaSearch {
  readonly timezone: string;
  readonly appointments: readonly AgendaAppointment[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
}

/**
 * Los turnos que esperan que el negocio los confirme, sobre todas las fechas.
 *
 * Misma forma que una busqueda —una pagina de turnos— porque se muestran igual;
 * lo que cambia es la pregunta: aca no hay un nombre que escribir. `from` es
 * desde cuando se listan: el comienzo de hoy en la zona del negocio.
 */
export interface AgendaPending {
  readonly timezone: string;
  readonly from: string;
  readonly appointments: readonly AgendaAppointment[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
}

/** Una franja del dia, en minutos desde la medianoche del negocio. */
export interface TimeWindow {
  readonly startMinute: number;
  readonly endMinute: number;
}

/**
 * Un dia de la vista semanal: sus turnos y, sobre todo, sus **huecos**.
 *
 * Los huecos los calcula el servidor con las mismas reglas y los mismos turnos
 * que lista: un hueco que la pantalla dice tener y el motor despues rechaza es
 * peor que no mostrarlo.
 */
export interface AgendaWeekDay {
  readonly date: string;
  /** 0 = domingo. */
  readonly weekday: number;
  /** El negocio no abre ese dia, o hay un cierre. */
  readonly closed: boolean;
  readonly windows: readonly TimeWindow[];
  readonly gaps: readonly TimeWindow[];
  readonly openMinutes: number;
  readonly busyMinutes: number;
  readonly freeMinutes: number;
  readonly appointments: readonly AgendaAppointment[];
}

export interface AgendaWeek {
  readonly timezone: string;
  /** El lunes de la semana. Lo decide el servidor, no el navegador. */
  readonly from: string;
  readonly to: string;
  readonly days: readonly AgendaWeekDay[];
}

/**
 * Horarios de un dia como los ve el panel: los libres y los ocupados.
 *
 * Es la misma cuenta que la pagina publica, con las reglas del negocio: sin
 * aviso minimo ni tope de anticipacion. `busy` esta para los negocios que
 * atienden fuera del horario publicado, donde no hay horarios libres que
 * ofrecer y hay que saber igual que esta tomado.
 */
export interface AgendaAvailabilityDay {
  readonly date: string;
  readonly slots: readonly AvailabilitySlot[];
  readonly busy?: readonly { readonly start: string; readonly end: string }[];
}

export interface AgendaAvailability {
  readonly timezone: string;
  readonly service: { readonly id: string; readonly name: string; readonly durationMinutes: number };
  readonly professionalId: string | null;
  readonly days: readonly AgendaAvailabilityDay[];
}

/** Una fila de un ranking de metricas (servicio o profesional). */
export interface MetricsRow {
  readonly id: string | null;
  readonly name: string;
  readonly count: number;
  /** Ausente cuando esta sesion no puede ver los importes del negocio. */
  readonly revenueCents?: number;
}

export interface MetricsTotals {
  readonly appointments: number;
  readonly completed: number;
  readonly cancelled: number;
  readonly noShow: number;
  readonly pending: number;
  readonly confirmed: number;
  /** Ausente cuando esta sesion no puede ver los importes del negocio. */
  readonly revenueCents?: number;
  readonly customers: number;
}

export interface MetricsResponse {
  readonly timezone: string;
  readonly period: { readonly days: number; readonly from: string; readonly to: string };
  /** Recortado a un profesional: la pantalla lo dice en lugar de mentir. */
  readonly scopedToProfessional: boolean;
  /**
   * Si esta respuesta trae importes. Cuando es `false` la API **no los mando**
   * —no es que la pantalla los esconda—, asi que no hay nada que dibujar.
   */
  readonly showsRevenue?: boolean;
  readonly totals: MetricsTotals;
  /** El periodo inmediatamente anterior, del mismo largo. */
  readonly previous: MetricsTotals;
  readonly daily: readonly {
    readonly date: string;
    readonly appointments: number;
    readonly completed: number;
    /** Ausente cuando esta sesion no puede ver los importes del negocio. */
    readonly revenueCents?: number;
  }[];
  readonly byService: readonly MetricsRow[];
  readonly byProfessional: readonly MetricsRow[];
  readonly byHour: readonly { readonly hour: number; readonly count: number }[];
  readonly byWeekday: readonly { readonly weekday: number; readonly count: number }[];
}

export interface SummaryResponse {
  readonly timezone: string;
  readonly date: string;
  readonly today: readonly AgendaAppointment[];
  readonly upcoming: readonly AgendaAppointment[];
  /** Lo que se viene: hoy y los proximos siete dias, por estado. */
  readonly counters: Record<string, number>;
  /**
   * Lo que ya paso: los ultimos treinta dias, por estado.
   *
   * Opcional por la misma razon que el resto: durante un despliegue el
   * navegador puede tener el bundle nuevo y la API todavia la version
   * anterior. Sin el, las tarjetas de lo cumplido muestran cero.
   */
  readonly recentCounters?: Record<string, number>;
  /**
   * La plata del negocio, o `null` cuando esta sesion no la puede ver.
   *
   * `null` y no un objeto en cero: hay que poder distinguir "no te
   * corresponde" de "todavia no facturaste nada". Opcional ademas porque
   * durante un despliegue el navegador puede tener el bundle nuevo y la API la
   * version anterior.
   */
  readonly revenue?: RevenueSummary | null;
}

/**
 * Lo que vale un grupo de turnos y cuanto de eso ya esta cobrado.
 *
 * Los dos numeros **no se suman**: lo cobrado es una parte de lo que vale, no
 * plata aparte.
 */
export interface RevenueBucket {
  readonly count: number;
  readonly valueCents: number;
  readonly collectedCents: number;
  /**
   * Cuantos de esos turnos no tienen precio congelado: su valor sale de la
   * lista de hoy, asi que el total es en parte una estimacion.
   */
  readonly estimatedCount: number;
}

export interface RevenueSummary {
  /** Turnos cumplidos de los ultimos 30 dias, hoy incluido. */
  readonly billed: RevenueBucket;
  /** Turnos en pie de hoy a siete dias. Todavia no entro: puede cancelarse. */
  readonly booked: RevenueBucket;
  /** Comprobantes que el cliente declaro y el negocio todavia no miro. */
  readonly awaitingReview: { readonly count: number; readonly amountCents: number };
}

/**
 * Una franja del horario de atencion.
 *
 * `professionalId` y `serviceId` en `null` es el horario del negocio. Cuando
 * alguno tiene valor, la franja es de esa persona o de ese servicio; nunca de
 * los dos a la vez.
 */
export interface HoursRule {
  readonly weekday: number;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly professionalId: string | null;
  readonly serviceId: string | null;
}

export interface AdminCustomer {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly appointmentCount: number;
  /** Las cumplidas: son las unicas que cuentan para una cortesia. */
  readonly completedCount?: number;
  readonly lastAppointmentAt: string | null;
  readonly createdAt: string;
  readonly loyalty?: CustomerLoyaltyProgress;
}

export interface CustomerLoyaltyProgress {
  /** Cuantas cumplio desde la ultima cortesia, sin pasarse del umbral. */
  readonly current: number;
  readonly threshold: number;
  readonly remaining: number;
  /** Ya se gano una que todavia no se le otorgo. */
  readonly eligible: boolean;
  /** Tiene una sin usar ahora mismo. */
  readonly hasPending: boolean;
}

/** Una pagina de la lista de clientes, con cuantos hay en total. */
export interface AdminCustomerPage {
  readonly customers: AdminCustomer[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
  /**
   * Como esta el programa del negocio. Viaja con la lista para que el boton de
   * cada fila sepa si hace algo, sin una segunda consulta.
   */
  readonly loyalty?: {
    readonly enabled: boolean;
    readonly configured: boolean;
    readonly threshold: number;
    readonly description: string;
  };
}

/* -------------------------------------------------------------------------- */
/* La ficha de una persona                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Cuantas veces vino, cuantas falto, desde cuando.
 *
 * Recortado a lo de quien atiende cuando quien mira es alguien del equipo: la
 * ficha lo dice con `scopedToProfessional` en lugar de dar numeros que no son
 * los del negocio sin avisar.
 */
export interface CustomerStats {
  readonly total: number;
  readonly completed: number;
  readonly cancelled: number;
  readonly noShow: number;
  /** Turnos que todavia no pasaron y siguen en pie. */
  readonly upcoming: number;
  readonly firstAt: string | null;
  readonly lastAt: string | null;
}

export interface AdminCustomerDetail {
  readonly customer: {
    readonly id: string;
    readonly name: string;
    readonly email: string | null;
    readonly phone: string | null;
    /** Las escribe el negocio y no las ve quien reserva. */
    readonly notes: string | null;
    /** Los valores de los campos propios, por su llave estable. */
    readonly customData: Record<string, CustomerFieldValue>;
    readonly createdAt: string;
  };
  /** Que campos propios declaro el negocio. Vacio: la ficha se ve como siempre. */
  readonly customerFields: CustomerField[];
  readonly stats: CustomerStats;
  readonly scopedToProfessional: boolean;
}

export interface CustomerHistoryItem {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: string;
  readonly cancelledBy: string | null;
  readonly notes: string | null;
  readonly serviceName: string;
  readonly professionalName: string | null;
}

export interface CustomerHistoryPage {
  readonly appointments: CustomerHistoryItem[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
}

/* -------------------------------------------------------------------------- */
/* Cortesias                                                                   */
/* -------------------------------------------------------------------------- */

export type RewardStatus = "pending" | "redeemed" | "cancelled";

export interface CustomerReward {
  readonly id: string;
  readonly status: RewardStatus;
  readonly statusLabel: string;
  readonly rewardKind: RewardKind;
  readonly rewardValue: number;
  /** El beneficio en palabras, como se prometio al otorgarlo. */
  readonly description: string;
  readonly threshold: number;
  readonly note: string | null;
  readonly grantedAt: string;
  readonly redeemedAt: string | null;
  /** El turno al que esta aplicada, cuando ya se engancho a uno. */
  readonly appointmentId: string | null;

  /*
   * Los importes solo existen cuando esta aplicada a un turno: el precio
   * depende del servicio que se elija, y adelantar un numero seria inventarlo.
   */
  readonly listPriceCents: number | null;
  readonly listPriceLabel: string | null;
  readonly discountCents: number;
  readonly discountLabel: string | null;
  readonly totalCents: number | null;
  readonly totalLabel: string | null;
}

/** Por que el boton no puede otorgar ahora mismo. */
export type RewardBlockedReason = "NOT_CONFIGURED" | "ALREADY_PENDING" | "NOT_ELIGIBLE";

export interface CustomerRewardsDetail {
  readonly customer: { readonly id: string; readonly name: string };
  readonly program: {
    readonly enabled: boolean;
    readonly configured: boolean;
    readonly threshold: number;
    readonly rewardKind: RewardKind;
    readonly rewardValue: number;
    readonly label: string;
    readonly description: string;
  };
  readonly completed: number;
  readonly progress: CustomerLoyaltyProgress;
  readonly pending: CustomerReward | null;
  readonly rewards: CustomerReward[];
  readonly canGrant: boolean;
  readonly blockedReason: RewardBlockedReason | null;
  /**
   * Los turnos a los que se le puede enganchar una cortesia marcada a mano.
   * Ya vienen filtrados: sin cancelados y sin los que llevan una.
   */
  readonly appointments?: CustomerAppointmentOption[];
}

export interface CustomerAppointmentOption {
  readonly id: string;
  readonly startsAt: string;
  readonly status: string;
  readonly serviceName: string;
  readonly servicePriceCents: number | null;
}

/* -------------------------------------------------------------------------- */
/* Auditoria                                                                   */
/* -------------------------------------------------------------------------- */

export interface AuditEvent {
  readonly id: string;
  readonly createdAt: string;
  readonly action: string;
  /** La accion en palabras. La arma el servidor: el catalogo vive en el dominio. */
  readonly actionLabel: string;
  readonly category: string;
  readonly categoryLabel: string;
  /** `user` | `customer` | `system` | `platform`. */
  readonly actorType: string;
  readonly actorTypeLabel: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly entityType: string;
  readonly entityId: string | null;
  readonly summary: string;
  readonly metadata: Record<string, unknown>;
  readonly ip: string | null;
}

export interface AuditPage {
  readonly events: AuditEvent[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
}

export interface AuditFilters {
  readonly categories: { readonly id: string; readonly label: string }[];
  readonly actions: { readonly action: string; readonly label: string; readonly category: string }[];
  readonly actors: {
    readonly actorId: string;
    readonly actorName: string | null;
    readonly actorType: string;
    readonly actorTypeLabel: string;
  }[];
  /** Cuanto tiempo se conserva el registro: lo anterior ya se borro. */
  readonly retentionDays: number;
}

export interface ScheduleExceptionItem {
  readonly id: string;
  readonly exceptionDate: string;
  readonly closed: boolean;
  readonly startMinute: number | null;
  readonly endMinute: number | null;
  readonly reason: string | null;
  /**
   * De quien es el cierre. Los dos vacios significan "todo el negocio", y son
   * excluyentes: una excepcion tiene un solo dueño.
   */
  readonly professionalId: string | null;
  readonly resourceId?: string | null;
}

/* -------------------------------------------------------------------------- */
/* Integraciones: avisos salientes (n8n, Make, Zapier)                         */
/* -------------------------------------------------------------------------- */

export interface WebhookDelivery {
  readonly id: string;
  readonly event: string;
  readonly status: "pending" | "delivered" | "failed";
  readonly attempts: number;
  readonly lastStatusCode: number | null;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly deliveredAt: string | null;
  readonly nextAttemptAt: string | null;
}

export interface WebhookEndpoint {
  readonly id: string;
  readonly url: string;
  readonly description: string | null;
  readonly events: string[];
  readonly active: boolean;
  /** Lo que se pega en n8n. Solo lo recibe un administrador. */
  readonly secret: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Los ultimos envios, el mas nuevo primero. */
  readonly deliveries: WebhookDelivery[];
}

export interface WebhookSettings {
  readonly endpoints: WebhookEndpoint[];
  /** El catalogo de eventos, con su nombre para mostrar. Lo manda la API. */
  readonly events: { readonly key: string; readonly label: string }[];
  readonly limit: number;
  readonly maxAttempts: number;
}

export interface WebhookTestResult {
  readonly ok: boolean;
  readonly statusCode: number | null;
  readonly error: string | null;
  readonly durationMs: number;
}


/* -------------------------------------------------------------------------- */
/* Caja: productos, ventas y cobros (D-065)                                    */
/* -------------------------------------------------------------------------- */

export type PaymentMethod = "cash" | "transfer" | "debit" | "credit" | "mercadopago" | "other";
export type QrMode = "dynamic" | "static";

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: "Efectivo",
  transfer: "Transferencia",
  debit: "Debito",
  credit: "Credito",
  mercadopago: "Mercado Pago (QR)",
  other: "Otro",
};

/** Los que se registran a mano, en el orden en que se ofrecen. */
export const MANUAL_PAYMENT_METHODS: readonly PaymentMethod[] = ["cash", "transfer", "debit", "credit", "other"];

export interface Product {
  readonly id: string;
  readonly name: string;
  readonly sku: string | null;
  readonly category: string | null;
  readonly description: string | null;
  readonly priceCents: number;
  readonly currency: string;
  readonly trackStock: boolean;
  readonly stockQuantity: number;
  readonly lowStockThreshold: number;
  readonly lowStock: boolean;
  readonly active: boolean;
  readonly displayOrder: number;
  /** Solo para quien administra. */
  readonly costCents?: number | null;
  readonly marginPercent?: number | null;
}

export interface StockMovement {
  readonly id: string;
  readonly kind: "restock" | "sale" | "sale_cancel" | "adjustment";
  readonly quantity: number;
  readonly balanceAfter: number;
  readonly saleId: string | null;
  readonly note: string | null;
  readonly createdAt: string;
}

export interface SalePayment {
  readonly id: string;
  readonly saleId: string;
  readonly method: PaymentMethod;
  readonly methodLabel: string;
  readonly status: "pending" | "approved" | "cancelled" | "refunded" | "failed";
  readonly amountCents: number;
  readonly currency: string;
  readonly reference: string | null;
  readonly tenderedCents: number | null;
  readonly changeCents: number | null;
  readonly qrMode: QrMode | null;
  readonly qrData: string | null;
  readonly expiresAt: string | null;
  readonly receivedByName: string | null;
  readonly approvedAt: string | null;
  readonly createdAt: string;
  readonly note: string | null;
}

export interface SaleItem {
  readonly id: string;
  readonly kind: "service" | "product" | "custom" | "pass";
  readonly productId: string | null;
  readonly serviceId: string | null;
  readonly description: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
  readonly discountCents: number;
  readonly totalCents: number;
  readonly professionalId: string | null;
}

export interface Sale {
  readonly id: string;
  readonly number: number;
  readonly status: "open" | "paid" | "cancelled";
  readonly customerId: string | null;
  readonly customerName: string | null;
  readonly appointmentId: string | null;
  readonly professionalId: string | null;
  readonly professionalName: string | null;
  readonly subtotalCents: number;
  readonly discountCents: number;
  readonly totalCents: number;
  readonly prepaidCents: number;
  readonly paidCents: number;
  readonly dueCents: number;
  readonly overpaidCents: number;
  readonly currency: string;
  readonly note: string | null;
  readonly createdByName: string | null;
  readonly createdAt: string;
  readonly paidAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancelReason: string | null;
  readonly items: readonly SaleItem[];
  readonly payments: readonly SalePayment[];
}

export interface SaleListItem {
  readonly id: string;
  readonly number: number;
  readonly status: "open" | "paid" | "cancelled";
  readonly totalCents: number;
  readonly dueCents: number;
  readonly customerId: string | null;
  readonly customerName: string | null;
  readonly professionalName: string | null;
  readonly appointmentId: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly summary: string;
  readonly methods: readonly PaymentMethod[];
}

export interface SalesPage {
  readonly sales: readonly SaleListItem[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
}

/** El resumen de cobro de un turno en la agenda: "Cobrar" o "Cobrado". */
export interface AppointmentSaleSummary {
  readonly id: string;
  readonly number: number;
  readonly status: "open" | "paid" | "cancelled";
  readonly totalCents: number;
  readonly dueCents: number;
}

/** Un cierre con arqueo (D-069). A ciegas llega sin lo esperado ni la diferencia. */
export interface CashClosing {
  readonly id: string;
  readonly number: number;
  readonly periodFrom: string;
  readonly closedAt: string;
  readonly countedCents: number;
  readonly leftInDrawerCents: number;
  readonly withdrawnCents: number;
  readonly closedByName: string | null;
  readonly note: string | null;
  readonly openingCents?: number;
  readonly cashInCents?: number;
  readonly cashOutCents?: number;
  readonly expectedCents?: number;
  readonly differenceCents?: number;
  readonly differenceKind?: "exact" | "over" | "short";
  readonly byMethod?: readonly { readonly method: string; readonly label: string; readonly amountCents: number; readonly count: number }[];
}

/** El cajon desde el ultimo cierre. `blind`: quien pregunta cuenta sin ver lo esperado. */
export interface CashDrawer {
  readonly periodFrom: string;
  readonly isFirst: boolean;
  readonly blind: boolean;
  readonly openingCents: number;
  readonly cashInCents?: number;
  readonly cashInCount?: number;
  readonly cashOutCents?: number;
  readonly cashOutCount?: number;
  readonly expectedCents?: number;
  readonly byMethod?: CashClosing["byMethod"];
  readonly lastClosing: CashClosing | null;
}

export interface CashSummary {
  readonly from: string;
  readonly to: string;
  readonly collectedCents: number;
  readonly prepaid: { readonly amountCents: number; readonly count: number };
  readonly refundedCents: number;
  readonly byMethod: readonly { readonly method: PaymentMethod; readonly label: string; readonly amountCents: number; readonly count: number }[];
  readonly sales: {
    readonly count: number;
    readonly totalCents: number;
    readonly serviceCents: number;
    readonly productCents: number;
    readonly customCents: number;
    readonly discountCents: number;
    readonly openCount: number;
    readonly dueCents: number;
    readonly averageTicketCents: number;
  };
  readonly daily: readonly { readonly date: string; readonly amountCents: number }[];
  readonly topProducts: readonly { readonly productId: string | null; readonly description: string; readonly quantity: number; readonly totalCents: number }[];
  readonly byProfessional: readonly {
    readonly professionalId: string;
    readonly name: string;
    readonly serviceCents: number;
    readonly productCents: number;
    readonly commissionPercent?: number | null;
    readonly productCommissionPercent?: number | null;
    readonly commissionCents?: number;
  }[];
}

export interface PendingCollections {
  readonly days: number;
  readonly appointments: readonly {
    readonly appointmentId: string;
    readonly startsAt: string;
    readonly customerId: string;
    readonly customerName: string;
    readonly customerPhone: string | null;
    readonly serviceName: string;
    readonly priceCents: number;
    readonly professionalName: string | null;
    readonly prepaidCents: number;
    readonly dueCents: number;
  }[];
  readonly openSales: readonly {
    readonly id: string;
    readonly number: number;
    readonly createdAt: string;
    readonly customerId: string | null;
    readonly customerName: string | null;
    readonly totalCents: number;
    readonly dueCents: number;
    readonly summary: string;
  }[];
  readonly totals: { readonly appointmentsCents: number; readonly openSalesCents: number };
}

export interface CustomerAccount {
  readonly dueCents: number;
  readonly openSales: number;
  readonly spentCents: number;
  readonly sales: readonly SaleListItem[];
}

/* -------------------------------------------------------------------------- */
/* Mercado Pago del negocio (D-066)                                            */
/* -------------------------------------------------------------------------- */

export interface MercadoPagoConfig {
  /** `oauth`: autorizo la aplicacion de TurnPro (D-070); `token`: pego el suyo. */
  readonly connection: "oauth" | "token";
  readonly tokenExpiresAt: string | null;
  readonly userId: string;
  readonly nickname: string | null;
  readonly email: string | null;
  readonly mode: QrMode;
  readonly externalPosId: string | null;
  readonly posName: string | null;
  readonly storeId: string | null;
  readonly qrTemplateUrl: string | null;
  readonly webhookConfigured: boolean;
  readonly connectedAt: string;
}

export interface MercadoPagoStatus {
  readonly connected: boolean;
  readonly config: MercadoPagoConfig | null;
  /** La instalacion tiene la aplicacion OAuth: se conecta con un boton. */
  readonly oauthAvailable: boolean;
  /** Conectado por OAuth y con los avisos de la aplicacion de TurnPro activos. */
  readonly automaticNotifications: boolean;
  readonly webhookUrl: string;
}

export interface MercadoPagoCajas {
  readonly stores: readonly { readonly id: string; readonly name: string; readonly externalId: string | null }[];
  readonly pos: readonly { readonly id: string; readonly name: string; readonly externalId: string | null; readonly storeId: string | null; readonly qrTemplateUrl: string | null }[];
  readonly suggestedExternalId: string;
}

/* -------------------------------------------------------------------------- */
/* Facturacion electronica (D-067)                                             */
/* -------------------------------------------------------------------------- */

export type IssuerIvaCondition = "monotributo" | "responsable_inscripto" | "exento";

export interface ArcaConfig {
  readonly cuit: string;
  readonly businessName: string;
  readonly ivaCondition: IssuerIvaCondition;
  readonly ivaConditionLabel: string;
  readonly pointOfSale: number;
  readonly environment: "homologacion" | "produccion";
  readonly address: string | null;
  readonly grossIncome: string | null;
  readonly activityStart: string | null;
  readonly defaultIvaRateId: number;
  readonly hasCsr: boolean;
  readonly csrCreatedAt: string | null;
  readonly certificate: { readonly subject: string; readonly validFrom: string; readonly validTo: string } | null;
}

export interface ArcaStatus {
  readonly configured: boolean;
  readonly hasKey: boolean;
  readonly hasCertificate: boolean;
  readonly certificateExpired: boolean;
  readonly certificateDaysLeft: number | null;
  readonly ready: boolean;
}

export interface FacturacionResponse {
  readonly config: ArcaConfig | null;
  readonly status: ArcaStatus;
  readonly catalogs: {
    readonly receptorConditions: readonly { readonly id: number; readonly label: string }[];
    readonly documentTypes: readonly { readonly id: number; readonly label: string }[];
  };
}

export interface Invoice {
  readonly id: string;
  readonly saleId: string | null;
  readonly associatedInvoiceId: string | null;
  readonly environment: "homologacion" | "produccion";
  readonly type: number;
  readonly typeLabel: string;
  readonly pointOfSale: number;
  readonly number: number;
  readonly formattedNumber: string;
  readonly status: "pending" | "authorized" | "rejected" | "unknown";
  readonly concept: number;
  readonly documentType: number;
  readonly documentNumber: number;
  readonly receptorName: string | null;
  readonly receptorIvaCondition: number;
  readonly issueDate: string;
  readonly totalCents: number;
  readonly netCents: number;
  readonly ivaCents: number;
  readonly ivaRateId: number | null;
  readonly cae: string | null;
  readonly caeExpiresOn: string | null;
  readonly observations: readonly { readonly code: number; readonly message: string }[];
  readonly error: string | null;
  readonly annulledAt: string | null;
  readonly createdAt: string;
  readonly qrUrl: string | null;
}

export interface InvoiceDetail {
  readonly invoice: Invoice;
  readonly issuer: {
    readonly cuit: string;
    readonly businessName: string;
    readonly ivaCondition: string;
    readonly address: string | null;
    readonly grossIncome: string | null;
    readonly activityStart: string | null;
  } | null;
  readonly items: readonly { readonly description: string; readonly quantity: number; readonly unitPriceCents: number; readonly discountCents: number; readonly totalCents: number }[];
  readonly saleDiscountCents: number;
}

/** Lo que va a cobrar el ticket de un turno, antes de crearlo. */
export interface CheckoutPreview {
  readonly appointmentId: string;
  readonly status: string;
  readonly customer: { readonly id: string; readonly name: string } | null;
  readonly professional: { readonly id: string; readonly name: string } | null;
  readonly line: {
    readonly kind: "service";
    readonly serviceId: string;
    readonly description: string;
    readonly quantity: number;
    readonly unitPriceCents: number;
    readonly discountCents: number;
    readonly totalCents: number;
    readonly rewardDiscountCents: number;
    readonly prepaidDiscountCents: number;
  };
  readonly prepaidCents: number;
  readonly existingSaleId: string | null;
  /** Los paquetes del cliente que sirven para este turno (D-071). */
  readonly passes: readonly {
    readonly id: string;
    readonly name: string;
    readonly kind: PassKind;
    /** `null`: un abono, sin limite de usos. */
    readonly remaining: number | null;
    readonly validUntil: string | null;
  }[];
  /** El paquete con que se pago este turno, si se pago con uno. */
  readonly paidWithPass: { readonly id: string; readonly name: string } | null;
}

/* -------------------------------------------------------------------------- */
/* Paquetes y abonos (D-071)                                                  */
/* -------------------------------------------------------------------------- */

export type PassKind = "sessions" | "unlimited";

/** Un paquete del catalogo del negocio. */
export interface PassPlan {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly kind: PassKind;
  readonly kindLabel: string;
  readonly sessions: number | null;
  readonly validityDays: number | null;
  readonly priceCents: number;
  readonly currency: string;
  /** Vacio: cubre cualquier servicio. */
  readonly serviceIds: readonly string[];
  readonly active: boolean;
  readonly displayOrder: number;
}

/** Lo que compro un cliente: congelado al venderlo. */
export interface CustomerPass {
  readonly id: string;
  readonly name: string;
  readonly kind: PassKind;
  readonly sessionsTotal: number | null;
  readonly sessionsUsed: number;
  readonly remaining: number | null;
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly serviceIds: readonly string[];
  readonly priceCents: number;
  readonly currency: string;
  readonly saleId: string | null;
  readonly status: "active" | "cancelled";
  readonly createdAt: string;
  readonly uses: readonly {
    readonly id: string;
    readonly appointmentId: string;
    readonly startsAt: string;
    readonly serviceName: string;
    readonly saleId: string | null;
  }[];
}
